import type { FC, MouseEvent, ReactNode } from 'react'

import { useRef, useState } from 'react'
import { cn } from '@/lib/utils'
import { band, navSections } from '@/constants/portfolio'
import { useFrame } from '@/hooks/use-frame'
import { audioEngine } from '@/lib/audio/engine'
import { scrollToSection, sectionAnchorY } from '@/lib/scroll-to'
import { useExperience } from '@/components/generals/experience-provider'

type Anchor = {
    id: string
    y: number
}

type Band = {
    top: number
    bottom: number
}

const Slogan: FC = (): ReactNode => (
    <p className='mono-label hidden text-[10.5px] wide:block' aria-label={band.tagline.join('. ')}>
        {band.tagline.map((line, index) => (
            <span key={line}>
                {index > 0 && <span className='px-2 opacity-50' aria-hidden='true'>|</span>}
                {line}
            </span>
        ))}
    </p>
)

// Fixed zine header: wordmark left, sections dead centre, slogan and Menu right.
const TopBar: FC = (): ReactNode => {
    const { menuOpen, setMenuOpen } = useExperience()
    const [active, setActive] = useState<string | null>(null)
    const [night, setNight] = useState(false)
    const measuredAt = useRef(0)
    const anchors = useRef<Anchor[]>([])
    const nightBands = useRef<Band[]>([])

    useFrame(state => {
        if (state.time - measuredAt.current > 0.5) {
            measuredAt.current = state.time

            anchors.current = navSections
                .map(section => ({ id: section.id, y: sectionAnchorY(section.id) }))
                .filter((anchor): anchor is Anchor => anchor.y !== null)

            nightBands.current = Array.from(document.querySelectorAll<HTMLElement>('[data-theme="night"]')).map(element => {
                const rect = element.getBoundingClientRect()
                return { top: rect.top + window.scrollY, bottom: rect.bottom + window.scrollY }
            })
        }

        const y = state.rawScrollY
        const probe = y + state.viewportH * 0.35
        let current: string | null = null

        for (const anchor of anchors.current) {
            if (anchor.y <= probe) current = anchor.id
        }

        if (current !== active) setActive(current)

        const line = y + 32
        const isNight = nightBands.current.some(item => line >= item.top && line < item.bottom)
        if (isNight !== night) setNight(isNight)
    })

    const go = (event: MouseEvent<HTMLAnchorElement>, id: string) => {
        event.preventDefault()
        audioEngine.cue('confirm')
        scrollToSection(id)
    }

    return (
        <header
            className={cn(
                'fixed inset-x-0 top-0 z-[80] border-b-2 transition-colors duration-300',
                night ? 'border-paper/15 bg-night text-paper' : 'border-ink bg-paper text-ink'
            )}
        >
            <div className='relative mx-auto flex h-14 max-w-[1680px] items-center justify-between gap-4 px-4 xs:px-6 tab:h-16 tab:px-8'>
                <a
                    href='#top'
                    onClick={event => go(event, 'top')}
                    className='display-type text-[20px] leading-none tracking-[-0.05em] tab:text-[22px]'
                    aria-label={`${band.name}, back to the top`}
                >
                    Bluntsheep
                </a>

                <nav aria-label='Sections' className='absolute left-1/2 top-1/2 hidden -translate-x-1/2 -translate-y-1/2 tab:block'>
                    <ul className='flex items-center gap-1'>
                        {navSections.map(section => {
                            const isActive = active === section.id
                            return (
                                <li key={section.id}>
                                    <a
                                        href={`#${section.id}`}
                                        onClick={event => go(event, section.id)}
                                        aria-current={isActive ? 'true' : undefined}
                                        className='group relative block px-3 py-2 text-[15px] font-semibold tracking-[-0.01em]'
                                    >
                                        <span className='relative z-10'>{section.label}</span>
                                        <span
                                            aria-hidden='true'
                                            className={cn(
                                                'absolute inset-x-2 bottom-0.5 h-[5px] origin-left -skew-x-12 transition-transform duration-300 ease-out',
                                                isActive ? 'scale-x-100 bg-bandana' : cn('scale-x-0 group-hover:scale-x-100', night ? 'bg-paper' : 'bg-ink')
                                            )}
                                        />
                                    </a>
                                </li>
                            )
                        })}
                    </ul>
                </nav>

                <div className='flex items-center gap-5'>
                    <Slogan />

                    <button
                        type='button'
                        onClick={() => {
                            audioEngine.cue('confirm')
                            setMenuOpen(!menuOpen)
                        }}
                        aria-expanded={menuOpen}
                        aria-controls='site-menu'
                        className={cn(
                            'group relative flex h-10 items-center gap-3 overflow-hidden px-4 transition-colors hover:text-paper',
                            night ? 'bg-paper text-ink' : 'bg-ink text-paper'
                        )}
                    >
                        <span
                            aria-hidden='true'
                            className='absolute -inset-y-1 -left-3 w-[calc(100%+24px)] -translate-x-[115%] -skew-x-12 bg-bandana transition-transform duration-300 ease-out group-hover:translate-x-0 group-focus-visible:translate-x-0'
                        />
                        <span className='mono-label relative text-xs'>Menu</span>
                        <span aria-hidden='true' className='relative flex w-5 flex-col gap-[4px]'>
                            <span className='h-[2px] w-full bg-current' />
                            <span className='h-[2px] w-3/4 bg-current' />
                            <span className='h-[2px] w-1/2 bg-current' />
                        </span>
                    </button>
                </div>
            </div>
        </header>
    )
}

export default TopBar
